import { getReadonlyListItemPill, getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { IxcPortfolioV1TransactionDetail, IxcRaglV1TransactionDetail, IxfAccountingV1TransactionDetail } from '@interxfi/types/generated/api';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { getAccuntingManualEntryTransactionListItems } from '../accounting/manual-entry/accounting-manual-entry-list-items';
import { getBillingChargeTransactionListItems, getBillingCustomerChargeTransactionListItems } from '../billing/charge/billing-charge-list-items';
import { getRaglManualTransactionListItems } from '../clearing-ledger/manual-entry/ragl-manual-entry-list-items';
import { getCorpactTransactionListItems } from '../corporate-action/action/corpact-action-list-items';
import { getCorpactCaAdminDtcPaymentTransactionListItems } from '../corporate-action/ca-admin/payment/ca-admin-payment-list-items';
import { getCnsSettlementTransactionListItems } from '../dtcc/cns/cns-shared-list-items';
import { getFundingBatchTransactionListItems } from '../funding/batch-transfer/funding-batch-transfer-list-items';
import { getFundingTransactionListItems } from '../funding/shared-funding-list-items';
import {
  getInventoryCloseTradingDayTransactionListItems,
  getInventorySettleTradingDayTransactionListItems,
  getInventoryTradeTransactionListItems,
} from '../inventory/shared-inventory-list-items';
import { getMutualFundTransactionListItems } from '../mutual-funds/fund-order/mutual-funds-fund-order-list-items';
import {
  getOrdersBatchAllocationAveragePriceTransactionListItems,
  getOrdersBatchAllocationCustomerTransactionListItems,
  getOrdersBatchRemainderInventoryTransactionListItems,
  getOrderTradeTransactionListItems,
} from '../order/order-list-items';
import { getPortfolioDividendPaymentTransactionListItems } from '../portfolio/shared-portfolio-list-items';
import { getClearingSweepsTransactionListItems } from '../sweeps/clearing/shared-clearing-sweeps-list-items';
import { getTradeNettingIndicationTransactionListItems } from '../trade/trade-list-items';
import { getJpmamMoneyMarketTransactionListItems } from '../vendor/jpmam/jpmam-shared-list-items';
import { TransactionTypePill } from './transaction-type-pill';
import type { TransactionSource } from './utils';

export function getTransactionDetailListItems(
  data: IxcPortfolioV1TransactionDetail | IxcRaglV1TransactionDetail | IxfAccountingV1TransactionDetail | undefined,
  source: TransactionSource,
  prefix = '',
) {
  const baseTypeNameColumn = {
    typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', undefined),
  } as const satisfies ReadonlyListItemRecord;

  const typePill = getReadonlyListItemPill(`${prefix}typePill`, 'Type', data ? <TransactionTypePill detail={data} source={source} /> : null);

  switch (data?.['!type']) {
    case 'exc.jpmam.v1.MoneyMarketTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Money Market'),
        typePill,
        jpmamMoneyMarketTransaction: {
          '!type': data['!type'],
          value: getJpmamMoneyMarketTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.billing.v1.ChargeTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Charge'),
        typePill,
        billingChargeTransaction: {
          '!type': data['!type'],
          value: getBillingChargeTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.billing.v1.CustomerChargeTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Customer Charge'),
        typePill,
        billingCustomerChargeTransaction: {
          '!type': data['!type'],
          value: getBillingCustomerChargeTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.funding.v1.FundingTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Funding'),
        typePill,
        fundingTransaction: {
          '!type': data['!type'],
          value: getFundingTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.funding.v1.FundingBatchTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Batch Transfer'),
        typePill,
        fundingBatchTransaction: {
          '!type': data['!type'],
          value: getFundingBatchTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.orders.v1.TradeTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Trade'),
        typePill,
        ordersTradeTransaction: {
          '!type': data['!type'],
          value: getOrderTradeTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.trade.v1.NettingIndicationTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Netting Indication'),
        typePill,
        tradeNettingIndicationTransaction: {
          '!type': data['!type'],
          value: getTradeNettingIndicationTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixf.accounting.v1.ManualEntryTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Manual Entry'),
        typePill,
        manualEntry: {
          '!type': data['!type'],
          value: getAccuntingManualEntryTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.portfolio.v1.DividendPaymentTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Dividend'),
        typePill,
        dividendPayment: {
          '!type': data['!type'],
          value: getPortfolioDividendPaymentTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.sweeps.clearing.v1.SweepsTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Sweep'),
        typePill,
        sweeps: {
          '!type': data['!type'],
          value: getClearingSweepsTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.orders.v1.BatchAllocationAvgPriceTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Batch Allocation'),
        typePill,
        batchAllocationAvgPriceTransaction: {
          '!type': data['!type'],
          value: getOrdersBatchAllocationAveragePriceTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.orders.v1.BatchAllocationCustomerTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Batch Allocation'),
        typePill,
        batchAllocationCustomerTransaction: {
          '!type': data['!type'],
          value: getOrdersBatchAllocationCustomerTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.orders.v1.BatchRemainderInventoryTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Batch Inventory'),
        typePill,
        batchOrderRemainderInventoryTransaction: {
          '!type': data['!type'],
          value: getOrdersBatchRemainderInventoryTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.ragl.v1.ManualTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Manual Entry'),
        typePill,
        raglManualTransaction: {
          '!type': data['!type'],
          value: getRaglManualTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.corpactions.v1.CorpactionTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Corporate Action'),
        typePill,
        corporateAction: {
          '!type': data['!type'],
          value: getCorpactTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.corpactions.caadmin.v1.DTCPaymentTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'DTC Payment'),
        typePill,
        corporateActionDtcPayment: {
          '!type': data['!type'],
          value: getCorpactCaAdminDtcPaymentTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.inventory.v1.InventoryTradeTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Inventory Trade'),
        typePill,
        inventoryTradeTransaction: {
          '!type': data['!type'],
          value: getInventoryTradeTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.inventory.v1.SettleTradingDayTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Settlement'),
        typePill,
        settleInventoryTradingDayTransaction: {
          '!type': data['!type'],
          value: getInventorySettleTradingDayTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.inventory.v1.CloseTradingDayTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Day Close'),
        typePill,
        closeInventoryTradingDayTransaction: {
          '!type': data['!type'],
          value: getInventoryCloseTradingDayTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case 'ixc.dtcc.cns.v1.SettlementTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'CNS'),
        typePill,
        cnsSettlementTransaction: {
          '!type': data['!type'],
          value: getCnsSettlementTransactionListItems(data.value, `${prefix}value.`),
        },
      };
    case 'ixc.mutualfunds.v1.FundTransaction':
      return {
        typeName: getReadonlyListItemString(`${prefix}typeName`, 'Type', 'Mutual Fund'),
        typePill,
        mutualFundTransaction: {
          '!type': data['!type'],
          value: getMutualFundTransactionListItems(data.value, `${prefix}value.`),
        },
      } as const satisfies ReadonlyListItemRecord;
    case undefined:
      return baseTypeNameColumn;
    default: {
      exhaustiveCheck(data?.['!type']);
      return baseTypeNameColumn;
    }
  }
}
